import { Box, Typography } from '@mui/material';

import { HEADER_HEIGHT } from '~/features/header/GlobalHeader.styles';

import { useStickyHeaders } from '../../hooks/StickyHeaderContext';
import { useStyles } from './StickyNotificationHeader.styles';

/**
 * StickyHeaderDisplay Component
 *
 * Internal component that renders the actual sticky header at the top of the page.
 * Positioned below the global header and only displays when there are visible headers
 * from the context. Uses theme colors for consistent styling.
 *
 * @returns Sticky header display or null if no headers should be shown
 */
const StickyHeaderDisplay = () => {
  const { visibleHeaders } = useStickyHeaders();
  const { classes } = useStyles();
  if (visibleHeaders.length === 0) {
    return null;
  }

  return (
    <div
      style={{
        top: HEADER_HEIGHT,
      }}
      className={classes.stickyHeaderContainer}
    >
      {visibleHeaders.map((header) => (
        <Box key={header.id} data-testid={`sticky--${header.id}`}>
          <Typography
            variant={
              header.type === 'section' ? 'body_medium' : 'small_body_medium'
            }
          >
            {header.label}
          </Typography>
        </Box>
      ))}
    </div>
  );
};

/**
 * StickyNotificationHeader Component
 *
 * This component should wrap any content that contains NotificationHeader components.
 *
 * Usage:
 * ```tsx
 * <StickyHeaderProvider>
 *   <NotificationHeader type="section" label="Section 1" />
 *   <Content />
 *   <NotificationHeader type="section" label="Section 2" />
 *   <MoreContent />
 * </StickyHeaderProvider>
 * ```
 *
 * @param props - Component props
 * @param props.children - Content that may contain StickyHeader components
 * @returns Wrapper with sticky header display management
 */
export const StickyNotificationHeader: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  return (
    <Box sx={{ position: 'relative' }}>
      <StickyHeaderDisplay />
      {children}
    </Box>
  );
};
